import { moveSlidingToggle } from './sliding-toggle.js';
import { initializeCollectionSheet } from './collection-browsing.js';
import { createPublicViewCache, publicViewContext } from './public-view-cache.js';
import { catalogueSources, mergeHydratedResults, productsForHydration } from './coffee-finder-feed.js';
import { createCheckboxIndicator } from './checkbox-indicator.js';
const STORAGE_KEY = 'sigma-coffee-taste-v1';
/*
 * QUIZ MOTION STORYBOARD
 *   0ms   question content is replaced; progress and controls stay immediate
 *   0ms   heading and choice group enter together, 4px travel, no row stagger
 * 200ms   question is settled
 * Results and intro transitions use a 60ms exit, then a 220ms grouped entrance.
 * Initial load, keyboard and reduced-motion navigation are immediate.
 */
const MOTION = {
  easing: 'cubic-bezier(0.23, 1, 0.32, 1)',
  exitDuration: 60,
  enterDuration: 220,
  questionDuration: 200,
  groupDelay: 20,
  maxDelay: 60,
  offset: 4,
  textBlur: 2,
};
const LOADING_PHRASES = [
  'Consulting The Library',
  'Finding your next chapter',
  'Reading between the taste notes',
  'Turning to your taste',
  'A little research for your next ritual',
  'Finding a story worth brewing',
];
const RESULTS = { initial: 9, more: 6, concurrentCards: 4 };
const STEP_LABELS = ['Brew', 'Flavour', 'Comfort', 'Serve'];

const element = (tag, text, className) => {
  const node = document.createElement(tag);
  if (text) node.textContent = text;
  if (className) node.className = className;
  return node;
};

async function init(root) {
  if (root.dataset.initialized) return;
  root.dataset.initialized = 'true';
  let questions, validAnswers, rankCoffees, profileFor;
  try {
    // Use Shopify's content-versioned URL so an older cached ranking module cannot
    // outlive an updated finder release.
    ({ questions, validAnswers, rankCoffees, profileFor } = await import(root.dataset.finderLogicUrl));
  } catch {
    delete root.dataset.initialized;
    const status = root.querySelector('[data-finder-status]');
    if (status) status.textContent = 'The coffee finder could not load. Please refresh or browse all coffee.';
    return;
  }
  // Decorative modules fail independently of the quiz and its results.
  const [waveModule, textModule] = await Promise.all([
    import(root.dataset.finderWaveUrl).catch(() => null),
    import(root.dataset.finderLoadingTextUrl).catch(() => null),
  ]);
  if (!root.isConnected) return;

  const view = root.querySelector('[data-finder-view]');
  const liveStatus = root.querySelector('[data-finder-status]');
  const intro = view.innerHTML;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const animations = new Set();
  let answers = {};
  const activeQuestions = () => questions.filter(question => question.key !== 'milk' || answers.brew?.[0] !== 'Filter');
  const filterAnswers = value => value.brew?.[0] === 'Filter' ? { ...value, milk: ['black'] } : value;
  let request = null;
  const catalogueCache = createPublicViewCache({ maxEntries: 1, maxBytes: 2_000_000 });
  const cardCache = createPublicViewCache({ maxEntries: 24, maxBytes: 3_000_000 });
  window.addEventListener('pagehide', () => {
    catalogueCache.clear();
    cardCache.clear();
  });
  let transitionId = 0;
  let modality = 'pointer';
  let mode = 'basic';
  let editing = false;
  let stopLoadingText = () => {};
  let stopLoadingWave = () => {};
  const stopLoading = () => {
    stopLoadingText();
    stopLoadingWave();
  };

  const icon = (name) => {
    const image = element('img');
    image.src = root.dataset[`${name}Icon`];
    image.alt = '';
    image.width = 20;
    image.height = 20;
    return image;
  };

  const button = (label, action, { primary = false, iconName } = {}) => {
    const node = element('button', '', `button button--compact coffee-finder__button${primary ? ' button--primary' : ''}`);
    node.type = 'button';
    if (iconName) node.append(icon(iconName));
    node.append(element('span', label));
    node.addEventListener('click', action);
    return node;
  };

  const reveal = (node, kind = 'content') => {
    node.dataset.finderReveal = kind;
    return node;
  };

  const announce = (message) => {
    liveStatus.textContent = message;
  };

  const cancelAnimations = () => {
    animations.forEach((animation) => animation.cancel());
    animations.clear();
  };

  const abortRequest = () => {
    stopLoading();
    request?.abort();
    request = null;
  };

  root.addEventListener('pointerdown', () => { modality = 'pointer'; });
  root.addEventListener('keydown', () => {
    modality = 'keyboard';
    cancelAnimations();
  });
  reducedMotion.addEventListener('change', cancelAnimations);

  const animate = (node, keyframes, options) => {
    const animation = node.animate(keyframes, options);
    animations.add(animation);
    animation.finished.catch(() => {}).finally(() => {
      animation.cancel();
      animations.delete(animation);
    });
    return animation;
  };

  const canAnimate = () => !reducedMotion.matches && modality !== 'keyboard' && typeof view.animate === 'function';

  function focusHeading() {
    const heading = view.querySelector('h1');
    if (!heading) return;
    heading.tabIndex = -1;
    const headerHeight = document.querySelector('.site-header')?.getBoundingClientRect().height || 0;
    root.style.setProperty('--finder-scroll-offset', `${headerHeight + 24}px`);
    heading.focus({ preventScroll: true });
    view.scrollIntoView({ block: 'start', behavior: 'instant' });
  }

  function enterState() {
    if (!canAnimate()) return;
    const targets = [...view.querySelectorAll('[data-finder-reveal]')].filter((node) => {
      const bounds = node.getBoundingClientRect();
      return bounds.top < window.innerHeight && bounds.bottom > 0;
    });
    const question = root.dataset.finderState === 'question';
    targets.forEach((node, index) => {
      const isText = node.dataset.finderReveal === 'text';
      animate(node, [
        { opacity: 0, transform: `translateY(${MOTION.offset}px)`, ...(isText && !question ? { filter: `blur(${MOTION.textBlur}px)` } : {}) },
        { opacity: 1, transform: 'translateY(0)', ...(isText && !question ? { filter: 'blur(0)' } : {}) },
      ], {
        duration: question ? MOTION.questionDuration : MOTION.enterDuration,
        delay: question ? 0 : Math.min(index * MOTION.groupDelay, MOTION.maxDelay),
        easing: MOTION.easing,
        fill: 'both',
      });
    });
  }

  async function changeState(render, { initial = false, immediate = false } = {}) {
    stopLoading();
    const current = ++transitionId;
    cancelAnimations();
    if (!initial && !immediate && canAnimate()) {
      // The short exit is a fade only: large product images never receive a blur.
      const exit = animate(view, [{ opacity: 1 }, { opacity: 0 }], {
        duration: MOTION.exitDuration,
        easing: MOTION.easing,
      });
      await exit.finished.catch(() => {});
    }
    if (current !== transitionId) return;
    cancelAnimations();
    render();
    if (!initial) focusHeading();
    if (!initial) enterState();
  }

  function setState(state) {
    root.dataset.finderState = state;
    if (state === 'loading') {
      root.setAttribute('aria-busy', 'true');
    } else {
      root.removeAttribute('aria-busy');
    }
    view.replaceChildren();
    announce('');
  }

  function readSavedProfile() {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
      return saved?.version === 1 && validAnswers(saved.answers) ? filterAnswers(saved.answers) : null;
    } catch {
      return null;
    }
  }

  function showIntro({ initial = false } = {}) {
    abortRequest();
    editing = false;
    return changeState(() => {
      setState('intro');
      view.innerHTML = intro;
      const start = view.querySelector('[data-start]');
      start.hidden = false;
      start.addEventListener('click', () => goToQuestion(0));
      view.querySelector('[data-duration]').hidden = false;
      const saved = readSavedProfile();
      if (saved) {
        const panel = reveal(element('div', '', 'coffee-finder__saved'));
        const copy = element('div');
        copy.append(element('p', 'Welcome back', 'coffee-finder__eyebrow'));
        copy.append(element('p', `Your ${profileFor(saved)} taste profile is ready.`, 'coffee-finder__copy'));
        const controls = element('div', '', 'coffee-finder__saved-actions');
        controls.append(button('Use saved profile', () => { answers = saved; showResults(); }));
        controls.append(button('Forget profile', () => {
          try {
            localStorage.removeItem(STORAGE_KEY);
            panel.remove();
            start.focus();
            announce('Saved taste profile removed from this browser.');
          } catch {
            announce('Unable to remove the saved profile. Try again.');
          }
        }));
        panel.append(copy, controls);
        view.append(panel);
      }
    }, { initial });
  }

  function goToQuestion(index) {
    abortRequest();
    return changeState(() => renderQuestion(index), { immediate: true });
  }

  function renderQuestion(index) {
    setState('question');
    const question = activeQuestions()[index];
    const selected = new Set(answers[question.key] || []);
    const form = element('form', '', 'coffee-finder__form');
    form.noValidate = true;

    const progress = element('div', '', 'coffee-finder__progress');
    const steps = element('ol', '', 'coffee-finder__steps');
    steps.setAttribute('aria-label', 'Quiz progress');
    function updateProgress() {
      steps.replaceChildren();
      activeQuestions().forEach((step, stepIndex) => {
        const item = element('li', STEP_LABELS[questions.indexOf(step)]);
        if (stepIndex === index) item.setAttribute('aria-current', 'step');
        item.dataset.complete = String(stepIndex < index);
        steps.append(item);
      });
    }
    progress.append(steps);

    const header = reveal(element('header', '', 'coffee-finder__question-header'), 'text');
    const heading = element('h1', question.title, 'coffee-finder__title');
    heading.id = `${root.id}-question-title`;
    const description = question.key === 'milk' && answers.brew?.[0] === 'Espresso'
      ? 'Milk makes a big difference to espresso. This helps us find the right sweetness, body and balance for your cup.'
      : question.description;
    header.append(heading, element('p', description, 'coffee-finder__copy'));

    const field = element('fieldset', '', 'coffee-finder__fieldset');
    field.setAttribute('aria-labelledby', heading.id);
    const helper = element('p', '', `coffee-finder__helper${question.multiple ? ' coffee-finder__helper--multiple' : ''}`);
    helper.id = `${root.id}-choice-help`;
    helper.setAttribute('aria-live', 'polite');
    const error = element('p', '', 'coffee-finder__validation');
    error.id = `${root.id}-choice-error`;
    error.setAttribute('role', 'alert');
    const options = reveal(element('div', '', 'coffee-finder__options'));
    const inputs = [];

    for (const [value, label, detail] of question.options) {
      const option = element('label', '', 'coffee-finder__option');
      const input = element('input');
      input.type = question.multiple ? 'checkbox' : 'radio';
      input.name = question.key;
      input.value = value;
      input.checked = selected.has(value);
      input.setAttribute('aria-describedby', `${helper.id} ${error.id}`);
      const text = element('span', '', 'coffee-finder__option-copy');
      text.append(element('span', label, 'coffee-finder__option-title'));
      text.append(element('span', detail, 'coffee-finder__option-detail'));
      option.append(input);
      if (question.multiple) option.append(createCheckboxIndicator());
      option.append(text);
      options.append(option);
      inputs.push(input);
      input.addEventListener('change', () => {
        if (question.multiple) {
          if (value === 'any') {
            selected.clear();
            if (input.checked) selected.add(value);
          } else {
            selected.delete('any');
            if (input.checked && selected.size < 2) selected.add(value);
            else selected.delete(value);
          }
        } else {
          selected.clear();
          selected.add(value);
        }
        const previousBrew = answers.brew?.[0];
        answers[question.key] = [...selected];
        if (question.key === 'brew') {
          // A hidden serving answer must never restrict filter matches or carry
          // over as an explicit choice when returning to another brew method.
          if (previousBrew === 'Filter' && value !== 'Filter') delete answers.milk;
          answers = filterAnswers(answers);
        }
        error.textContent = '';
        inputs.forEach((node) => node.removeAttribute('aria-invalid'));
        updateSelection();
      });
    }

    function updateSelection() {
      updateProgress();
      const finish = index === activeQuestions().length - 1 || (editing && validAnswers(answers));
      next.querySelector('span').textContent = finish ? 'See my matches' : 'Continue';
      for (const input of inputs) {
        input.checked = selected.has(input.value);
        input.disabled = Boolean(question.multiple && selected.size >= 2 && !selected.has(input.value) && input.value !== 'any');
      }
      if (question.multiple) {
        helper.textContent = selected.size === 2
          ? 'Great, we’ll prioritise these flavours. Deselect one to try another.'
          : 'Choose up to two flavours, or select Surprise me.';
      } else if (question.key === 'adventure') {
        helper.textContent = 'Keep it easy: 1–2 · A little interesting: 2–4 · Push my palate: 4–5';
      } else {
        helper.textContent = 'Choose one option.';
      }
    }

    field.append(helper, options, error);
    const actions = element('div', '', 'coffee-finder__actions coffee-finder__actions--navigation');
    actions.append(button('Back', () => index === 0 ? showIntro() : goToQuestion(index - 1), { iconName: 'back' }));
    const finish = (editing && validAnswers(answers)) || index === activeQuestions().length - 1;
    const next = button(finish ? 'See my matches' : 'Continue', () => {}, {
      primary: true,
      iconName: finish ? 'sparkle' : undefined,
    });
    next.type = 'submit';
    actions.append(next);
    form.append(header, field, actions);
    view.append(progress, form);
    updateSelection();

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      if (!selected.size) {
        error.textContent = question.multiple ? 'Choose a flavour or select Surprise me to continue.' : 'Choose an option to continue.';
        inputs.forEach((input) => input.setAttribute('aria-invalid', 'true'));
        inputs[0]?.focus();
        return;
      }
      answers[question.key] = [...selected];
      answers = filterAnswers(answers);
      const currentQuestions = activeQuestions();
      if ((editing || index === currentQuestions.length - 1) && validAnswers(answers)) showResults();
      else if (editing) goToQuestion(currentQuestions.findIndex(item => !answers[item.key]?.length));
      else goToQuestion(Math.min(index + 1, currentQuestions.length - 1));
    });
  }

  async function loadProducts(signal) {
    const cacheKey = `${publicViewContext()}\n${root.dataset.feed}`;
    const cached = catalogueCache.get(cacheKey);
    if (cached) return cached;

    for (const source of catalogueSources(root.dataset.feed, window.location.origin)) {
      try {
        let responseBytes = 0;
        const products = [];
        const visited = new Set();
        let url = source.url;
        while (url) {
          if (visited.size >= 100 || url.origin !== window.location.origin || visited.has(url.href)) throw new Error('Invalid catalogue pagination');
          visited.add(url.href);
          const response = await fetch(url, { signal, credentials: 'same-origin' });
          if (!response.ok) throw new Error('Catalogue unavailable');
          const markup = await response.text();
          responseBytes += new TextEncoder().encode(markup).byteLength;
          if (responseBytes > source.maxBytes) throw new Error('Catalogue response is too large');
          const document = new DOMParser().parseFromString(markup, 'text/html');
          const feed = document.querySelector('[data-finder-feed]');
          if (!feed || (source.mode === 'compact' && feed.dataset.finderProfileVersion !== '2')) {
            throw new Error('Catalogue unavailable');
          }
          for (const item of feed.querySelectorAll('[data-finder-product]')) {
            const profile = item.querySelector('[data-profile]');
            if (!profile) throw new Error('Catalogue profile unavailable');
            const product = JSON.parse(profile.textContent);
            if (!product?.id) throw new Error('Catalogue profile unavailable');
            if (source.mode === 'compact') {
              if (product.version !== 2 || typeof product.url !== 'string' || !Array.isArray(product.variants)) {
                throw new Error('Catalogue profile unavailable');
              }
            } else {
              const card = item.querySelector('[data-card]')?.content;
              const showcase = card?.querySelector('[data-product-card-showcase]');
              const variants = JSON.parse(card?.querySelector('[data-product-card-variants]')?.textContent || '[]');
              if (!card || !showcase || !Array.isArray(variants)) throw new Error('Catalogue card unavailable');
              product.url = showcase.dataset.productCardUrl || card.querySelector('[data-product-card-link]')?.getAttribute('href');
              product.card = card;
              product.variants = variants;
            }
            products.push(product);
          }
          url = feed.dataset.next ? new URL(feed.dataset.next, window.location.origin) : null;
          if (url) url.searchParams.set('view', source.mode === 'compact' ? 'coffee-finder-index' : 'coffee-finder');
        }
        if (signal.aborted) throw new DOMException('Catalogue cancelled', 'AbortError');
        catalogueCache.set(cacheKey, products, responseBytes);
        return products;
      } catch (error) {
        if (signal.aborted || error?.name === 'AbortError' || source.mode === 'legacy') throw error;
      }
    }
    throw new Error('Catalogue unavailable');
  }

  async function loadProductCard(product, signal) {
    const url = new URL(product.url, window.location.origin);
    if (url.origin !== window.location.origin) throw new Error('Invalid Finder card URL');
    url.searchParams.set('view', 'coffee-finder-card');
    const cacheKey = `${publicViewContext()}\n${url.href}`;
    const cached = cardCache.get(cacheKey);
    if (cached) return cached;

    const response = await fetch(url, { signal, credentials: 'same-origin' });
    if (!response.ok) throw new Error('Finder card unavailable');
    const markup = await response.text();
    const responseBytes = new TextEncoder().encode(markup).byteLength;
    if (responseBytes > 250_000) throw new Error('Finder card response is too large');
    const document = new DOMParser().parseFromString(markup, 'text/html');
    const wrapper = document.querySelector('[data-finder-card]');
    const card = wrapper?.querySelector('[data-card]')?.content;
    const identity = wrapper?.dataset.finderCardProductId;
    if (!card || String(identity || '') !== String(product.id)) {
      throw new Error('Finder card identity mismatch');
    }
    const variants = JSON.parse(card.querySelector('[data-product-card-variants]')?.textContent || '[]');
    if (!Array.isArray(variants)) throw new Error('Finder card variants unavailable');
    const hydrated = { id: product.id, card, variants };
    cardCache.set(cacheKey, hydrated, responseBytes);
    return hydrated;
  }

  async function hydrateResults(results, signal) {
    const products = productsForHydration(results);
    const hydrated = [];
    for (let index = 0; index < products.length; index += RESULTS.concurrentCards) {
      if (signal.aborted) throw new DOMException('Catalogue cancelled', 'AbortError');
      hydrated.push(...await Promise.all(products.slice(index, index + RESULTS.concurrentCards).map((product) => product.card
        ? { id: product.id, card: product.card, variants: product.variants }
        : loadProductCard(product, signal))));
    }
    return mergeHydratedResults(
      results,
      new Map(hydrated.map((product) => [product.id, product])),
    );
  }

  function renderLoading() {
    setState('loading');
    const header = reveal(element('header', '', 'coffee-finder__state-header'), 'text');
    header.append(element('h1', 'Matching your coffee…', 'coffee-finder__title'));
    header.append(element('p', 'Finding available coffees for your brew method, favourite flavours and comfort level.', 'coffee-finder__copy'));
    const loading = reveal(element('div', '', 'coffee-finder__loading'));
    const wave = waveModule?.createWavePhysicsLoader({ reducedMotion });
    const loadingText = element('p', '', 'coffee-finder__copy coffee-finder__loading-text');
    if (wave) loading.append(wave.element);
    loading.append(loadingText);
    loading.setAttribute('aria-hidden', 'true');
    view.append(header, loading, button('Edit my answers', () => goToQuestion(0)));
    if (wave) {
      wave.start();
      stopLoadingWave = wave.stop;
    }
    if (textModule) {
      stopLoadingText = textModule.startLoadingPhrases(loadingText, { phrases: LOADING_PHRASES, reducedMotion });
    } else {
      loadingText.textContent = LOADING_PHRASES[0];
    }
    announce('Finding available coffee matches.');
  }

  async function showResults() {
    answers = filterAnswers(answers);
    if (!validAnswers(answers)) return goToQuestion(0);
    abortRequest();
    const current = new AbortController();
    request = current;
    await changeState(renderLoading);
    if (request !== current) return;
    const timeout = window.setTimeout(() => current.abort(), 30000);
    try {
      const products = await loadProducts(current.signal);
      if (request !== current) return;
      const rankedResults = rankCoffees(products, answers);
      if (request !== current) return;
      await changeState(() => renderResults(rankedResults));
    } catch {
      if (request !== current) return;
      await changeState(() => {
        setState('error');
        const header = reveal(element('header', '', 'coffee-finder__state-header'), 'text');
        header.append(element('p', 'Your answers are ready', 'coffee-finder__eyebrow'));
        header.append(element('h1', 'Let’s try that again', 'coffee-finder__title'));
        header.append(element('p', 'Unable to load the current coffee collection. Your answers are still here. Try again to see available matches.', 'coffee-finder__copy'));
        const actions = reveal(element('div', '', 'coffee-finder__actions'));
        actions.append(button('Try again', showResults, { primary: true }));
        actions.append(button('Edit my answers', () => goToQuestion(0)));
        view.append(header, actions);
        announce('Unable to load coffee matches. Your answers have been kept.');
      });
    } finally {
      window.clearTimeout(timeout);
    }
  }

  function renderResults(results) {
    const pool = [];
    const identities = new Set();
    const addToPool = (product, group) => {
      const identity = String(product?.id || '');
      if (!identity || identities.has(identity)) return;
      identities.add(identity);
      pool.push({ product, group, rank: pool.length });
    };
    results.best.forEach((product) => addToPool(product, 'best'));
    addToPool(results.safe, 'safe');
    addToPool(results.wildcard, 'wildcard');
    const total = pool.length;
    setState('results');
    editing = true;
    const profile = profileFor(answers);
    const titles = {
      'Espresso & Milk': 'Espresso with sweetness and body',
      Comfort: 'Comforting coffees, easy to love',
      'Funky & Fermented': 'A taste for the unexpected',
      Explorer: 'Coffees for a curious palate',
      'Washed & Elegant': 'Delicate flavours, full of character',
      'Bright Filter': 'Bright, fruit-forward filter coffees',
      Balanced: 'A little balance in every cup',
    };
    const hero = reveal(element('header', '', 'coffee-finder__result-header'), 'text');

    hero.append(element('h1', total ? titles[profile] : 'No coffees match this combination today', 'coffee-finder__title'));
    hero.append(element('p', total ? 'Available coffees matched to your taste.' : 'Try a different brew method or comfort level to explore more of the current collection.', 'coffee-finder__copy'));
    if (!total) hero.append(button('Edit my answers', () => goToQuestion(0), { primary: true }));
    const profileLabel = element('p', `${profile} taste profile`, 'coffee-finder__profile-label');
    hero.append(profileLabel);
    const answerList = element('div', '', 'coffee-finder__answer-list');
    answerList.setAttribute('role', 'group');
    answerList.setAttribute('aria-label', 'Your answers. Select an answer to edit it.');
    activeQuestions().forEach((question, index) => {
      const label = answers[question.key].map((value) => question.options.find((option) => option[0] === value)[1]).join(' + ');
      const edit = element('button', '', 'button button--compact coffee-finder__answer');
      edit.type = 'button';
      const editIcon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      editIcon.setAttribute('viewBox', '0 0 20 20');
      editIcon.setAttribute('class', 'coffee-finder__answer-edit');
      editIcon.setAttribute('aria-hidden', 'true');
      editIcon.setAttribute('focusable', 'false');
      const pencil = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      pencil.setAttribute('d', 'M12.5 3.5a2.12 2.12 0 0 1 3 3L7 15l-4 1 1-4 8.5-8.5ZM11 5l3 3');
      editIcon.append(pencil);
      edit.append(element('span', label), editIcon);
      edit.addEventListener('click', () => goToQuestion(index));
      edit.setAttribute('aria-label', `Edit ${STEP_LABELS[questions.indexOf(question)].toLowerCase()}: ${label}`);
      answerList.append(edit);
    });
    hero.append(answerList);
    const saveRow = reveal(element('div', '', 'coffee-finder__save-row'));
    const saved = readSavedProfile();
    const alreadySaved = saved && questions.every(({ key }) =>
      answers[key].length === saved[key].length && answers[key].every((value) => saved[key].includes(value))
    );
    const saveStatus = element('p', alreadySaved ? 'This taste profile is saved on this browser.' : 'Keep this taste profile for your next visit on this browser.', 'coffee-finder__helper');
    saveStatus.setAttribute('role', 'status');
    const save = button(alreadySaved ? 'Taste profile saved' : 'Save my taste profile', () => {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ version: 1, answers }));
        save.querySelector('span').textContent = 'Taste profile saved';
        save.disabled = true;
        saveStatus.textContent = 'Saved on this browser. You can use it again or remove it on the quiz start page.';
      } catch {
        saveStatus.textContent = 'Unable to save on this browser. Your current matches are still available.';
      }
    });
    save.disabled = Boolean(alreadySaved);
    saveRow.append(save, saveStatus);
    if (document.documentElement.hasAttribute('data-for-you-enabled')) {
      const accountSave = button('Save to my account', async () => {
        if (!window.SigmaForYou?.readyForSave) return;
        accountSave.disabled = true;
        try {
          const result = await window.SigmaForYou.saveQuiz(structuredClone(answers));
          saveStatus.textContent = result.message;
        } finally {
          accountSave.disabled = false;
        }
      });
      accountSave.dataset.forYouQuizSave = '';
      accountSave.hidden = !window.SigmaForYou?.readyForSave;
      saveRow.append(accountSave);
    }
    view.append(hero);

    if (!total) {
      view.append(saveRow);
      announce('No available coffees match this combination. You can edit or save your taste profile.');
      return;
    }

    const toolbar = reveal(element('div', '', 'coffee-finder__toolbar'));
    const heading = element('div');
    const resultHeading = element('h2', 'Best matches', 'coffee-finder__section-title');
    const resultCount = element('p', '', 'coffee-finder__copy');
    resultCount.setAttribute('role', 'status');
    heading.append(resultHeading, resultCount);
    const modes = element('div', '', 'coffee-finder__modes');
    modes.setAttribute('role', 'group');
    modes.setAttribute('aria-label', 'Viewing mode');
    const modeIndicator = element('span', '', 'coffee-finder__mode-indicator');
    modeIndicator.setAttribute('aria-hidden', 'true');
    modes.append(modeIndicator);
    for (const [value, label, iconName] of [['basic', 'Basic', 'growth'], ['advanced', 'Coffee Nerd', 'graduate']]) {
      const control = element('button', '', 'coffee-finder__mode');
      control.type = 'button';
      control.dataset.mode = value;
      control.append(icon(iconName), element('span', label));
      control.addEventListener('click', () => { mode = value; updateMode(); });
      modes.append(control);
    }
    toolbar.append(heading, modes);
    view.append(toolbar);

    const collator = new Intl.Collator(document.documentElement.lang || 'en-GB', { sensitivity: 'base' });
    const cleanText = (value) => typeof value === 'string' ? value.trim() : '';
    const minimumPrice = (product) => {
      const prices = (Array.isArray(product.variants) ? product.variants : [])
        .filter((variant) => variant?.available === true && variant.priceAmount !== null && variant.priceAmount !== '' &&
          Number.isFinite(Number(variant.priceAmount)) && Number(variant.priceAmount) >= 0)
        .map((variant) => Number(variant.priceAmount));
      return prices.length ? Math.min(...prices) : null;
    };
    const roastOrder = (product, rested) => {
      const variants = (product.variants || []).filter((variant) => variant.available === true);
      const ready = variants.filter((variant) => variant.readyNow === true);
      const candidates = rested && ready.length ? ready : variants;
      const dates = candidates.map((variant) => {
        const value = variant.roastDate;
        if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
        const timestamp = Date.parse(`${value}T00:00:00Z`);
        return Number.isFinite(timestamp) && new Date(timestamp).toISOString().slice(0, 10) === value ? timestamp : null;
      }).filter((value) => value !== null);
      return { ready: ready.length > 0, date: dates.length ? (rested ? Math.min(...dates) : Math.max(...dates)) : null };
    };
    const selections = { sort: 'best' };
    let shown = RESULTS.initial;
    let resultRequest = null;
    let resultRevision = 0;

    const controls = reveal(element('div', '', 'coffee-finder__controls'));
    controls.setAttribute('role', 'group');
    controls.setAttribute('aria-label', 'Sort coffee matches');
    const sortContent = root.querySelector('[data-finder-sort-template]').content.cloneNode(true);
    const sortTrigger = sortContent.querySelector('[data-finder-sort-open]');
    const sortDialog = sortContent.querySelector('[data-finder-sort-dialog]');
    const sortTitle = sortContent.querySelector('[data-finder-sort-title]');
    sortDialog.id = `${root.id}-sort-dialog`;
    sortTitle.id = `${root.id}-sort-title`;
    sortDialog.setAttribute('aria-labelledby', sortTitle.id);
    sortTrigger.setAttribute('aria-controls', sortDialog.id);
    controls.append(sortContent);
    view.append(controls);
    const sheet = initializeCollectionSheet({
      trigger: sortTrigger,
      dialog: sortDialog,
      panel: sortDialog.querySelector('[data-finder-sort-panel]'),
      backdrop: sortDialog.querySelector('[data-finder-sort-dismiss]'),
      closeButton: sortDialog.querySelector('[data-finder-sort-close]'),
    });
    const sortOptions = [
      ['best', 'Best match', 'Closest to your taste profile', 'Sort by'],
      ['price-asc', 'Price: low to high', 'Lowest-priced bags first', 'Lowest price'],
      ['price-desc', 'Price: high to low', 'Highest-priced bags first', 'Highest price'],
      ['freshest', 'Freshest roast', 'Most recent available roast dates first', 'Freshest roast'],
      ['rested', 'Rested first', 'Rested & Ready first, then earlier roast dates', 'Rested first'],
      ['name', 'Roaster / name', 'Roasters and coffee names A to Z', 'Roaster / name'],
    ];
    const optionButtons = sortOptions.map(([value, label, description, triggerLabel]) => {
      const option = element('button', '', 'collection-sort-option');
      option.type = 'button';
      const copy = element('span', '', 'collection-sort-option__copy');
      copy.append(element('strong', label), element('small', description));
      const selector = element('span', '', 'collection-sort-option__selector');
      selector.setAttribute('aria-hidden', 'true');
      option.append(copy, selector);
      option.setAttribute('aria-current', String(value === selections.sort));
      option.addEventListener('click', () => {
        sheet.close();
        if (value === selections.sort) return;
        selections.sort = value;
        shown = RESULTS.initial;
        sortTrigger.querySelector('[data-finder-sort-label]').textContent = triggerLabel;
        sortTrigger.setAttribute('aria-label', `Sort by, currently ${label}`);
        optionButtons.forEach((button, index) => button.setAttribute('aria-current', String(sortOptions[index][0] === value)));
        updateResults();
      });
      sortDialog.querySelector('[data-finder-sort-options]').append(option);
      return option;
    });
    sortDialog.addEventListener('keydown', (event) => {
      const index = optionButtons.indexOf(document.activeElement);
      if (index < 0) return;
      let next;
      if (['ArrowDown', 'ArrowRight'].includes(event.key)) next = (index + 1) % optionButtons.length;
      else if (['ArrowUp', 'ArrowLeft'].includes(event.key)) next = (index - 1 + optionButtons.length) % optionButtons.length;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = optionButtons.length - 1;
      if (next !== undefined) { event.preventDefault(); optionButtons[next].focus(); }
    });

    const resultArea = element('div', '', 'coffee-finder__result-area');
    view.append(resultArea);

    function addCards(items, target) {
      const grid = element('div', '', 'collection-products coffee-finder__results');
      for (const product of items) {
        const article = reveal(element('article', '', 'coffee-finder__result'));
        const card = product.card.cloneNode(true);
        const showcase = card.querySelector('[data-product-card-showcase]');
        // Restrict the existing card controller before it initializes: prices, links and pickers
        // all use the same compatible available variants, including after size/date changes.
        showcase.dataset.selectedVariantId = String(product.variants[0].id);
        card.querySelector('[data-product-card-variants]').textContent = JSON.stringify(product.variants);
        article.append(card);
        const reason = element('div', '', 'coffee-finder__reason');
        reason.append(element('h3', 'Why it fits', 'coffee-finder__reason-title'));
        const list = element('ul');
        // Three concise reasons keep the basic result scannable. The full profile remains available in Nerd mode.
        for (const item of product.reasons.slice(0, 3)) list.append(element('li', item));
        reason.append(list);
        article.append(reason);
        grid.append(article);
      }
      target.append(grid);
      return grid;
    }

    view.append(saveRow);

    function updateMode() {
      moveSlidingToggle(modeIndicator, mode === 'advanced' ? 1 : 0);
      root.dataset.finderMode = mode;
      modes.dataset.finderModeSwitcher = mode;
      modes.querySelectorAll('button').forEach((control) => {
        control.setAttribute('aria-pressed', String(control.dataset.mode === mode));
      });
      view.querySelectorAll('[data-product-card-showcase]').forEach((card) => {
        const advanced = mode === 'advanced';
        card.dataset.productCardMode = mode;
        card.dataset.productCardProfileFill = advanced ? 'on' : 'off';
        for (const panel of card.querySelectorAll('[data-product-advanced-facts], [data-product-advanced-reveal]')) {
          panel.inert = !advanced;
          panel.setAttribute('aria-hidden', String(!advanced));
        }
        const summary = card.querySelector('[data-product-basic-summary]');
        if (summary) {
          summary.inert = advanced;
          summary.setAttribute('aria-hidden', String(advanced));
        }
      });
    }
    updateMode();

    const sortedEntries = () => {
      const filtered = pool;
      if (selections.sort === 'best') {
        return [...filtered].sort((left, right) => {
          const scoreDifference = Number(right.product.score) - Number(left.product.score);
          if (scoreDifference) return scoreDifference;
          const leftId = String(left.product.id);
          const rightId = String(right.product.id);
          return leftId < rightId ? -1 : leftId > rightId ? 1 : 0;
        });
      }
      return [...filtered].sort((left, right) => {
        if (selections.sort === 'name') {
          return collator.compare(cleanText(left.product.vendor), cleanText(right.product.vendor)) ||
            collator.compare(cleanText(left.product.title), cleanText(right.product.title)) || left.rank - right.rank;
        }
        if (selections.sort === 'freshest' || selections.sort === 'rested') {
          const rested = selections.sort === 'rested';
          const leftRoast = roastOrder(left.product, rested);
          const rightRoast = roastOrder(right.product, rested);
          if (rested && leftRoast.ready !== rightRoast.ready) return leftRoast.ready ? -1 : 1;
          if (leftRoast.date === null) return rightRoast.date === null ? left.rank - right.rank : 1;
          if (rightRoast.date === null) return -1;
          return (rested ? leftRoast.date - rightRoast.date : rightRoast.date - leftRoast.date) || left.rank - right.rank;
        }
        const leftPrice = minimumPrice(left.product);
        const rightPrice = minimumPrice(right.product);
        if (leftPrice === null) return rightPrice === null ? left.rank - right.rank : 1;
        if (rightPrice === null) return -1;
        const difference = selections.sort === 'price-desc' ? rightPrice - leftPrice : leftPrice - rightPrice;
        return difference || left.rank - right.rank;
      });
    };

    async function updateResults({ focusIndex = null } = {}) {
      const parentRequest = request;
      if (!parentRequest || parentRequest.signal.aborted) return;
      resultRequest?.abort();
      const current = new AbortController();
      resultRequest = current;
      const revision = ++resultRevision;
      const cancel = () => current.abort();
      parentRequest.signal.addEventListener('abort', cancel, { once: true });
      const matches = sortedEntries();
      const defaultPresentation = selections.sort === 'best';
      const visible = defaultPresentation
        ? [...matches.filter(({ group }) => group === 'best').slice(0, shown), ...matches.filter(({ group }) => group !== 'best')]
        : matches.slice(0, shown);
      const hasMore = defaultPresentation
        ? matches.filter(({ group }) => group === 'best').length > shown
        : matches.length > shown;
      resultHeading.textContent = defaultPresentation && matches.some(({ group }) => group === 'best') ? 'Best matches' : 'Your matches';
      resultCount.textContent = `${total} available ${total === 1 ? 'coffee matches' : 'coffees match'} your answers, ${selections.sort === 'best' ? 'ordered by fit.' : 'sorted to your preference.'}`;
      sortTrigger.dispatchEvent(new CustomEvent('sigma:button-loading', { bubbles: true, detail: { loading: true } }));
      resultArea.setAttribute('aria-busy', 'true');
      const pending = element('p', 'Updating matches…', 'coffee-finder__helper coffee-finder__results-status');
      pending.setAttribute('role', 'status');
      resultArea.replaceChildren(pending);

      const timeout = window.setTimeout(() => current.abort(), 30000);
      try {
        const hydrated = await hydrateResults({ best: visible.map(({ product }) => product) }, current.signal);
        if (request !== parentRequest || current.signal.aborted || revision !== resultRevision || !resultArea.isConnected) return;
        const hydratedById = new Map(hydrated.best.map((product) => [String(product.id), product]));
        const fragment = document.createDocumentFragment();
        if (defaultPresentation) {
          const best = visible.filter(({ group }) => group === 'best').map(({ product }) => hydratedById.get(String(product.id))).filter(Boolean);
          if (best.length) addCards(best, fragment);
          for (const [group, title, description] of [
            ['safe', 'Safe pick', 'A beginner-friendly alternative within your preferences.'],
            ['wildcard', 'Wildcard', 'Something more exploratory, within your chosen comfort range.'],
          ]) {
            const products = visible.filter((entry) => entry.group === group)
              .map(({ product }) => hydratedById.get(String(product.id))).filter(Boolean);
            if (!products.length) continue;
            const header = element('header', '', 'coffee-finder__group-header');
            header.append(element('h3', title, 'coffee-finder__section-title'), element('p', description, 'coffee-finder__copy'));
            fragment.append(header);
            addCards(products, fragment);
          }
        } else {
          addCards(visible.map(({ product }) => hydratedById.get(String(product.id))).filter(Boolean), fragment);
        }
        if (hasMore) {
          const moreRow = element('div', '', 'coffee-finder__more');
          const remaining = defaultPresentation
            ? matches.filter(({ group }) => group === 'best').length - shown
            : matches.length - shown;
          const moreStatus = element('p', `${remaining} more ${remaining === 1 ? 'match' : 'matches'} available.`, 'coffee-finder__helper');
          moreStatus.setAttribute('role', 'status');
          const more = button('Show more matches', () => {
            const previousCount = defaultPresentation
              ? Math.min(shown, matches.filter(({ group }) => group === 'best').length)
              : Math.min(shown, matches.length);
            shown += RESULTS.more;
            updateResults({ focusIndex: modality === 'keyboard' ? previousCount : null });
          });
          moreRow.append(more, moreStatus);
          fragment.append(moreRow);
        }
        resultArea.replaceChildren(fragment);
        resultArea.removeAttribute('aria-busy');
        updateMode();
        view.dispatchEvent(new CustomEvent('sigma:collection-view:updated', { bubbles: true, detail: { view } }));
        if (focusIndex !== null) {
          const cards = resultArea.querySelectorAll('.coffee-finder__result');
          (cards[focusIndex]?.querySelector('[data-product-card-link]') || resultArea.querySelector('.coffee-finder__more button'))
            ?.focus({ preventScroll: true });
        }
        announce(`${matches.length} coffee ${matches.length === 1 ? 'match' : 'matches'} ready.`);
      } catch (error) {
        if (request !== parentRequest || resultRequest !== current || revision !== resultRevision || !resultArea.isConnected) return;
        resultArea.removeAttribute('aria-busy');
        const failed = element('div', '', 'coffee-finder__empty');
        failed.append(
          element('h3', 'Unable to update matches', 'coffee-finder__section-title'),
          element('p', 'Your taste profile and sort order are still here. Try loading these matches again.', 'coffee-finder__copy'),
          button('Try again', updateResults, { primary: true }),
        );
        resultArea.replaceChildren(failed);
        announce('Unable to update coffee matches. Try again.');
      } finally {
        window.clearTimeout(timeout);
        if (resultRequest === current) {
          sortTrigger.dispatchEvent(new CustomEvent('sigma:button-loading', { bubbles: true, detail: { loading: false } }));
        }
        parentRequest.signal.removeEventListener('abort', cancel);
      }
    }
    updateResults();
  }

  // Page and theme-editor lifecycle: no timers or stale requests survive a removed section.
  const onPageHide = () => { abortRequest(); cancelAnimations(); };
  const onSectionUnload = (event) => {
    if (!event.target.contains(root)) return;
    abortRequest();
    cancelAnimations();
    reducedMotion.removeEventListener('change', cancelAnimations);
    document.removeEventListener('shopify:section:unload', onSectionUnload);
    window.removeEventListener('pagehide', onPageHide);
  };
  document.addEventListener('shopify:section:unload', onSectionUnload);
  window.addEventListener('pagehide', onPageHide, { once: true });
  showIntro({ initial: true });
}

document.querySelectorAll('[data-coffee-finder]').forEach(init);
document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('[data-coffee-finder]').forEach(init);
});
